
import { useRef, useEffect, useState, useCallback } from "react";
import Hls from "hls.js";
import type { VideoState, VideoType, Participant, VideoBookmark, SubtitleTrack } from "@/lib/room-state";
import type { ReactionIcon } from "@/lib/room-state";
import { getActiveCue } from "@/lib/subtitles";
import { formatTime, extractYouTubeId } from "@/lib/video-utils";
import { REACTION_ICONS } from "@/components/reaction-icons";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Gauge,
  Lock,
  ExternalLink,
  AlertCircle,
  Film,
  Bookmark,
  Subtitles,
  SkipBack,
  SkipForward,
  RotateCcw,
  Repeat,
  Clock,
  X,
  ChevronUp,
  Settings,
  PictureInPicture2,
} from "lucide-react";

export interface FloatingReactionItem {
  id: string;
  icon: ReactionIcon;
  customUrl?: string;
  userName: string;
}

interface VideoPlayerProps {
  videoState: VideoState;
  canControl: boolean;
  participants: Participant[];
  currentUserId: string;
  bookmarks: VideoBookmark[];
  subtitles: SubtitleTrack | null;
  floatingReactions: FloatingReactionItem[];
  onPlay: (time: number) => void;
  onPause: (time: number) => void;
  onSeek: (time: number) => void;
  onSpeedChange: (rate: number) => void;
  onAddBookmark?: (time: number, label: string) => void;
  onProgressPing?: (currentTime: number, playbackState: "playing" | "paused" | "buffering") => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string | HTMLElement,
        options: {
          videoId?: string;
          playerVars?: Record<string, unknown>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
            onError?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (s: number, allow: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setPlaybackRate: (r: number) => void;
  getPlaybackRate: () => number;
  loadVideoById: (id: string) => void;
  setVolume: (v: number) => void;
  mute: () => void;
  unMute: () => void;
  destroy?: () => void;
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function VideoPlayer({
  videoState,
  canControl,
  bookmarks,
  subtitles,
  floatingReactions,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
  onAddBookmark,
  onProgressPing,
  onTimeUpdate,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ytRef = useRef<YTPlayer | null>(null);
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const apiLoadingRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [theater, setTheater] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeCue, setActiveCue] = useState<string | null>(null);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarkLabel, setBookmarkLabel] = useState("");
  const [loop, setLoop] = useState(false);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [pipActive, setPipActive] = useState(false);
  const pipSupported = typeof document !== "undefined" && "pictureInPictureEnabled" in document && (document as any).pictureInPictureEnabled;
  const ytReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState("default");
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const ttsQueueRef = useRef<string[]>([]);
  const lastSpokenCueRef = useRef<string | null>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ytId = videoState.type === "youtube" ? extractYouTubeId(videoState.url) : null;

  // ---- Load YouTube IFrame API once ----
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.YT) return;
    if (apiLoadingRef.current) return;
    apiLoadingRef.current = true;
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  }, []);

  // ---- YouTube player lifecycle ----
  useEffect(() => {
    if (videoState.type !== "youtube" || !ytId) {
      // cleanup any existing YT player
      if (ytRef.current) {
        try {
          ytRef.current.destroy?.();
        } catch {
          /* noop */
        }
        ytRef.current = null;
        setReady(false);
      }
      return;
    }

    // If the player already exists, just load the new video (avoids
    // destroy+recreate which can corrupt the container DOM and crash React).
    if (ytRef.current && typeof ytRef.current.loadVideoById === "function") {
      try {
        setBuffering(true);
        setError(null);
        ytRef.current.loadVideoById(ytId);
        return;
      } catch {
        // fall through to recreate
        ytRef.current = null;
        setReady(false);
      }
    }

    if (!window.YT || !window.YT.Player) {
      // wait for API
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev?.();
        initYt();
      };
      return;
    }
    initYt();

    function initYt() {
      if (!ytContainerRef.current) return;
      setError(null);

      // Create a fresh inner div for the player. YouTube's API replaces this
      // div with an iframe, so on video switch we need a clean div (the old
      // one was replaced by an iframe and is no longer a valid mount point).
      const playerDiv = document.createElement("div");
      playerDiv.style.width = "100%";
      playerDiv.style.height = "100%";
      ytContainerRef.current.innerHTML = "";
      ytContainerRef.current.appendChild(playerDiv);

      // Timeout: if YT doesn't fire onReady within 8s, show error
      if (ytReadyTimeoutRef.current) clearTimeout(ytReadyTimeoutRef.current);
      ytReadyTimeoutRef.current = setTimeout(() => {
        if (!ytRef.current) return;
        // Check if player is actually ready by testing getCurrentTime
        try {
          if (typeof ytRef.current.getCurrentTime === "function") {
            ytRef.current.getCurrentTime();
            setReady(true);
          }
        } catch {
          setError("YouTube player failed to load. Try refreshing or pick another video.");
          setBuffering(false);
        }
      }, 8000);

      ytRef.current = new window.YT!.Player(playerDiv, {
        videoId: ytId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          controls: 0,
          disablekb: 1,
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
        events: {
          onReady: (e) => {
            if (ytReadyTimeoutRef.current) clearTimeout(ytReadyTimeoutRef.current);
            setReady(true);
            setBuffering(false);
            setDuration(e.target.getDuration() || 0);
            e.target.setVolume(Math.round(volume * 100));
            if (muted) e.target.mute();
            else e.target.unMute();
            setTimeout(() => {
              if (!ytRef.current) return;
              if (videoState.playing) e.target.playVideo();
              else e.target.pauseVideo();
              if (videoState.currentTime > 0) e.target.seekTo(videoState.currentTime, true);
              e.target.setPlaybackRate(playbackRate);
            }, 300);
          },
          onStateChange: (e) => {
            if (e.data === 1) {
              setIsPlaying(true);
              setBuffering(false);
              setDuration(e.target.getDuration() || 0);
            } else if (e.data === 2) {
              setIsPlaying(false);
            } else if (e.data === 3) {
              setBuffering(true);
            } else if (e.data === 0) {
              setIsPlaying(false);
              if (loop) {
                e.target.seekTo(0, true);
                e.target.playVideo();
              }
            }
          },
          onError: () => {
            if (ytReadyTimeoutRef.current) clearTimeout(ytReadyTimeoutRef.current);
            setError("This YouTube video could not be loaded. It may be private, region-locked, or embed-restricted.");
            setBuffering(false);
          },
        },
      });
    }

    return () => {
      // Clear any pending ready timeout to prevent stale getCurrentTime calls
      if (ytReadyTimeoutRef.current) { clearTimeout(ytReadyTimeoutRef.current); ytReadyTimeoutRef.current = null; }
      // Only null the ref and clear DOM if we're switching AWAY from YouTube
      // entirely (type changed to non-youtube). If only the videoId changed,
      // the loadVideoById path handles it — don't destroy the player.
      if (videoState.type !== "youtube") {
        if (ytRef.current) {
          try { ytRef.current.stopVideo?.(); } catch { /* noop */ }
          ytRef.current = null;
          setReady(false);
          setBuffering(false);
          setError(null);
        }
        if (ytContainerRef.current) {
          ytContainerRef.current.innerHTML = "";
        }
      }
    };
  }, [videoState.type, ytId]);

  // ---- HLS / native video lifecycle ----
  useEffect(() => {
    if (videoState.type === "youtube") return;
    if (!videoState.url) return;
    const video = videoRef.current;
    if (!video) return;

    setError(null);
    setReady(false);

    // cleanup previous hls
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (videoState.type === "hls") {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoState.url;
      } else if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hlsRef.current = hls;
        hls.loadSource(videoState.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => setReady(true));
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) {
            setError(`Stream error: ${data.details || data.type}`);
          }
        });
      } else {
        setError("HLS is not supported in this browser.");
      }
    } else {
      // direct mp4/webm + twitch/vimeo (twitch/vimeo fall back to a direct link notice)
      video.src = videoState.url;
      setReady(true);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute("src");
      try {
        video.load();
      } catch {
        /* noop */
      }
    };
  }, [videoState.url, videoState.type]);

  // ---- React to external videoState changes (sync from host/others) ----
  useEffect(() => {
    if (!ready) return;
    // Apply external videoState changes — but DON'T seek if only the speed changed.
    // We compare the current player time vs the broadcast currentTime to decide.
    if (videoState.type === "youtube" && ytRef.current) {
      const t = typeof ytRef.current.getCurrentTime === "function" ? ytRef.current.getCurrentTime() : 0;
      // Only seek if currentTime differs by more than 3s (avoids seeking on speed-only updates)
      if (Math.abs(t - videoState.currentTime) > 3 && typeof ytRef.current.seekTo === "function") {
        ytRef.current.seekTo(videoState.currentTime, true);
      }
      if (videoState.playing && !isPlaying && typeof ytRef.current.playVideo === "function") {
        ytRef.current.playVideo();
      } else if (!videoState.playing && isPlaying && typeof ytRef.current.pauseVideo === "function") {
        ytRef.current.pauseVideo();
      }
      if (Math.abs(playbackRate - videoState.playbackRate) > 0.01 && typeof ytRef.current.setPlaybackRate === "function") {
        ytRef.current.setPlaybackRate(videoState.playbackRate);
        setPlaybackRate(videoState.playbackRate);
      }
    } else if (videoRef.current && videoState.type !== "youtube") {
      const v = videoRef.current;
      // Only seek if currentTime differs by more than 3s
      if (Math.abs(v.currentTime - videoState.currentTime) > 3) {
        v.currentTime = videoState.currentTime;
      }
      if (videoState.playing && v.paused) v.play().catch(() => {});
      else if (!videoState.playing && !v.paused) v.pause();
      if (Math.abs(v.playbackRate - videoState.playbackRate) > 0.01) {
        v.playbackRate = videoState.playbackRate;
        setPlaybackRate(videoState.playbackRate);
      }
    }
  }, [videoState.updatedAt, ready]);

  // ---- Time tracking + progress ping ----
  useEffect(() => {
    if (!ready) return;
    const id = setInterval(() => {
      if (videoState.type === "youtube" && ytRef.current) {
        const t = typeof ytRef.current.getCurrentTime === "function" ? ytRef.current.getCurrentTime() : 0;
        const d = (typeof ytRef.current.getDuration === "function" && ytRef.current.getDuration()) || 0;
        setCurrentTime(t);
        setDuration(d);
        onTimeUpdate?.(t, d);
        if (videoState.playing) onProgressPing?.(t, "playing");
      } else if (videoRef.current) {
        const v = videoRef.current;
        setCurrentTime(v.currentTime);
        setDuration(v.duration || 0);
        onTimeUpdate?.(v.currentTime, v.duration || 0);
        if (!v.paused) onProgressPing?.(v.currentTime, v.readyState < 3 ? "buffering" : "playing");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [ready, videoState.type, videoState.playing, onProgressPing, onTimeUpdate]);

  // ---- Subtitles ----
  useEffect(() => {
    if (!subtitles) {
      setActiveCue(null);
      lastSpokenCueRef.current = null;
      return;
    }
    const id = setInterval(() => {
      const cue = getActiveCue(subtitles, currentTime);
      setActiveCue(cue);
      // TTS: speak the cue if it's new and TTS is enabled
      if (ttsEnabled && cue && cue !== lastSpokenCueRef.current) {
        lastSpokenCueRef.current = cue;
        try {
          const utterance = new SpeechSynthesisUtterance(cue);
          utterance.rate = playbackRate || 1;
          speechSynthesis.cancel();
          speechSynthesis.speak(utterance);
        } catch {
          /* TTS not available */
        }
      }
      if (!cue) lastSpokenCueRef.current = null;
    }, 250);
    return () => clearInterval(id);
  }, [subtitles, currentTime, ttsEnabled, playbackRate]);

  // ---- Auto-hide controls ----
  const pokeControls = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    pokeControls();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [pokeControls]);

  // ---- Handlers ----
  function togglePlay() {
    if (!canControl) return;
    if (videoState.type === "youtube" && ytRef.current) {
      const t = typeof ytRef.current.getCurrentTime === "function" ? ytRef.current.getCurrentTime() : 0;
      if (videoState.playing) onPause(t);
      else onPlay(t);
    } else if (videoRef.current) {
      const v = videoRef.current;
      if (videoState.playing) onPause(v.currentTime);
      else onPlay(v.currentTime);
    }
  }

  function handleSeek(e: React.ChangeEvent<HTMLInputElement>) {
    if (!canControl) return;
    const t = Number(e.target.value);
    onSeek(t);
  }

  function skip(delta: number) {
    if (!canControl) return;
    const base = videoState.type === "youtube" ? ytRef.current?.getCurrentTime() ?? 0 : videoRef.current?.currentTime ?? 0;
    onSeek(Math.max(0, base + delta));
  }

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    if (videoState.type === "youtube" && ytRef.current) {
      if (next) {
        ytRef.current.mute();
      } else {
        ytRef.current.unMute();
        // Also restore volume if it was at 0
        if (volume === 0) {
          setVolume(1);
          ytRef.current.setVolume(100);
        }
      }
    } else if (videoRef.current) {
      videoRef.current.muted = next;
      if (!next && volume === 0) {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  }

  function handleVolume(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Number(e.target.value);
    setVolume(v);
    if (videoState.type === "youtube" && ytRef.current) {
      ytRef.current.setVolume(Math.round(v * 100));
      if (v > 0 && muted) {
        setMuted(false);
        ytRef.current.unMute();
      }
    } else if (videoRef.current) {
      videoRef.current.volume = v;
      if (v > 0 && muted) {
        setMuted(false);
        videoRef.current.muted = false;
      }
    }
  }

  function changeRate(r: number) {
    if (!canControl) return;
    onSpeedChange(r);
    setPlaybackRate(r);
    // Apply rate directly to the player
    if (videoState.type === "youtube" && ytRef.current) {
      ytRef.current.setPlaybackRate(r);
    } else if (videoRef.current) {
      videoRef.current.playbackRate = r;
    }
    setShowRateMenu(false);
  }

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setTheater(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setTheater(false)).catch(() => {});
    }
  }

  // Native Picture-in-Picture — pops the video out into a floating window.
  // Only works on native <video> elements (not YouTube iframes).
  async function togglePip() {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setPipActive(false);
      } else {
        await v.requestPictureInPicture();
        setPipActive(true);
      }
    } catch { /* user dismissed or unsupported */ }
  }

  useEffect(() => {
    const onPipChange = () => setPipActive(!!document.pictureInPictureElement);
    document.addEventListener("enterpictureinpicture", onPipChange);
    document.addEventListener("leavepictureinpicture", onPipChange);
    return () => {
      document.removeEventListener("enterpictureinpicture", onPipChange);
      document.removeEventListener("leavepictureinpicture", onPipChange);
    };
  }, []);

  useEffect(() => {
    const onFs = () => setTheater(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ---- Double-click to skip 10s forward/backward ----
  function handleDoubleClick(e: React.MouseEvent) {
    if (!canControl) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clickX = e.clientX - rect.left;
    const isLeftHalf = clickX < rect.width / 2;
    const base = videoState.type === "youtube" ? ytRef.current?.getCurrentTime() ?? 0 : videoRef.current?.currentTime ?? 0;
    onSeek(Math.max(0, base + (isLeftHalf ? -10 : 10)));
  }

  function addBookmark() {
    if (!onAddBookmark || !canControl) return;
    const label = bookmarkLabel.trim() || `Mark at ${formatTime(currentTime)}`;
    onAddBookmark(currentTime, label);
    setBookmarkLabel("");
    setShowBookmarks(false);
  }

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const showEmptyState = !videoState.url;

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden flex items-center justify-center group ${
        theater ? "h-screen" : "aspect-video"
      }`}
      style={{
        background: "radial-gradient(900px 600px at 20% 20%, rgba(20,40,80,0.18), transparent 60%), radial-gradient(700px 500px at 80% 80%, rgba(40,20,70,0.14), transparent 60%), #0B0D10",
      }}
      onMouseMove={pokeControls}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onDoubleClick={handleDoubleClick}
    >
      {/* YouTube mount — uses a stable wrapper div. The YouTube API replaces
          the inner div with an iframe, so we create a fresh inner div each
          time in the effect (not managed by React) to avoid DOM conflicts. */}
      {videoState.type === "youtube" && (
        <div className="w-full h-full absolute inset-0 pointer-events-none overflow-hidden">
          <div ref={ytContainerRef} className="w-full h-full [&_iframe]:pointer-events-none [&_iframe]:w-full [&_iframe]:h-full" />
        </div>
      )}

      {/* Native / HLS video */}
      {videoState.type !== "youtube" && videoState.url && (
        <video
          ref={(el) => {
            videoRef.current = el;
            setVideoEl(el);
          }}
          className="w-full h-full"
          playsInline
          loop={loop}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => setBuffering(false)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        />
      )}

      {/* Empty state — just the icon + text, no surrounding box */}
      {showEmptyState && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          <Film className="w-12 h-12 mb-3 text-muted-foreground/60" />
          <p className="text-sm font-medium text-muted-foreground">No video loaded</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {canControl ? "Paste a link or browse the catalogue above." : "Waiting for the host to load a video."}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 p-6 text-center">
          <AlertCircle className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground mb-1 max-w-md">{error}</p>
          {videoState.url && (
            <a
              href={videoState.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open original in a new tab
            </a>
          )}
        </div>
      )}

      {/* Buffering indicator */}
      {buffering && !error && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-secondary border border-white/10 px-4 py-1.5 rounded-full text-xs font-medium">
          <RotateCcw className="w-3.5 h-3.5 animate-spin" />
          Buffering…
        </div>
      )}

      {/* Floating emoji reactions overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {floatingReactions.map((r) => {
          const reaction = REACTION_ICONS[r.icon];
          // Use a deterministic position based on the reaction ID so it
          // doesn't shift on re-renders (Math.random causes position changes)
          const hash = r.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
          const left = 10 + (hash % 80);
          return (
            <div
              key={r.id}
              className="absolute bottom-6 animate-float-up"
              style={{ left: `${left}%` }}
            >
              <div className="flex flex-col items-center gap-0.5">
                {r.customUrl ? (
                  <img
                    src={r.customUrl}
                    alt={r.userName}
                    className="w-6 h-6 object-contain"
                  />
                ) : reaction ? (
                  <span className="text-2xl leading-none">{reaction.emoji}</span>
                ) : null}
                <span className="text-[9px] font-medium text-white/80 bg-black/40 px-1.5 py-0.5 rounded">
                  {r.userName}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Subtitle overlay */}
      {activeCue && showCaptions && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 max-w-[90%] text-center pointer-events-none">
          <span className="inline-block glass-strong bg-black/60 text-white text-sm px-4 py-2 rounded-xl whitespace-pre-line border-white/15">
            {activeCue}
          </span>
        </div>
      )}

      {/* Controls overlay */}
      {videoState.url && !error && (
        <div
          className={`absolute inset-0 flex flex-col justify-between transition-opacity duration-200 ${
            showControls || !isPlaying ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Top bar: title + lock + bookmarks */}
          <div className="flex items-center justify-between p-3">
            <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
            <div className="flex items-center gap-2 min-w-0">
              {!canControl && (
                <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-black/50 px-2 py-1 rounded">
                  <Lock className="w-3 h-3" />
                  Host-only controls
                </span>
              )}
              <span className="text-[10px] font-mono text-white/60 bg-black/40 px-2 py-1 rounded uppercase">
                {videoState.type}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {/* Captions toggle — toggles subtitle display or YouTube CC */}
              <button
                onClick={() => {
                  if (videoState.type === "youtube" && ytRef.current && typeof (ytRef.current as any).setOption === "function") {
                    try { (ytRef.current as any).setOption("captions", "track", showCaptions ? {} : { languageCode: "en" }); } catch { /* noop */ }
                  }
                  setShowCaptions((s) => !s);
                }}
                className={`h-7 px-2 flex items-center gap-1 text-[11px] font-medium rounded-md transition-colors ${
                  showCaptions ? "text-primary bg-primary/20" : "text-white/70 hover:bg-white/10"
                }`}
                title={showCaptions ? "Captions ON" : "Captions OFF"}
              >
                <Subtitles className="w-3.5 h-3.5" />
              </button>
              {/* TTS toggle — only when subtitles are loaded */}
              {subtitles && (
                <button
                  onClick={() => {
                    const next = !ttsEnabled;
                    setTtsEnabled(next);
                    if (!next) {
                      try { speechSynthesis.cancel(); } catch {}
                    }
                  }}
                  className={`h-7 px-2 flex items-center gap-1 text-[11px] font-medium rounded-md transition-colors ${
                    ttsEnabled ? "text-primary bg-primary/20" : "text-white/70 hover:bg-white/10"
                  }`}
                  title={ttsEnabled ? "TTS ON — click to disable" : "TTS OFF — click to enable text-to-speech"}
                >
                  <Volume2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">TTS</span>
                </button>
              )}
            </div>
          </div>

          {/* Center play/pause button — clickable circle only (not the area around it) */}
          <div className="flex-1 flex items-center justify-center pointer-events-none">
            {canControl && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canControl) togglePlay(); }}
                className="w-16 h-16 flex items-center justify-center rounded-full glass-strong text-white pointer-events-auto hover:scale-105 transition-all"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 fill-current ml-1" />}
              </button>
            )}
          </div>

          {/* Bottom controls */}
          <div className="p-3 pt-6 space-y-2 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none rounded-b-[14px] hidden" />
            {/* Progress bar */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-white/80 w-10 text-right">
                {formatTime(currentTime)}
              </span>
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
                disabled={!canControl}
                className="flex-1 h-1 accent-primary disabled:opacity-40"
                style={{
                  background: `linear-gradient(to right, var(--color-primary) ${progressPct}%, rgba(255,255,255,0.2) ${progressPct}%)`,
                  borderRadius: "2px",
                }}
              />
              <span className="text-[10px] font-mono text-white/80 w-10">
                {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={togglePlay}
                disabled={!canControl}
 className="w-8 h-8 flex items-center justify-center text-white transition-colors disabled:opacity-40"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
              </button>
              <button
                onClick={() => skip(-10)}
                disabled={!canControl}
 className="w-8 h-8 flex items-center justify-center text-white transition-colors disabled:opacity-40"
                title="Back 10s"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              <button
                onClick={() => skip(10)}
                disabled={!canControl}
 className="w-8 h-8 flex items-center justify-center text-white transition-colors disabled:opacity-40"
                title="Forward 10s"
              >
                <SkipForward className="w-4 h-4" />
              </button>

              {/* Volume — click icon to toggle mute, click again to show slider */}
              <div className="flex items-center gap-1 relative">
                <button
                  onClick={() => {
                    if (showVolumeSlider) {
                      setShowVolumeSlider(false);
                    } else {
                      toggleMute();
                    }
                  }}
 className="w-8 h-8 flex items-center justify-center text-white transition-colors"
                  title={muted ? "Unmute" : "Mute"}
                >
                  {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                {showVolumeSlider && (
                  <div className="flex items-center gap-2 absolute bottom-9 left-0 bg-black/90 border border-white/10 rounded-md px-3 py-2">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={muted ? 0 : volume}
                      onChange={handleVolume}
                      className="w-20 h-1 accent-white"
                    />
                    <button
                      onClick={() => setShowVolumeSlider(false)}
                      className="text-white/60 hover:text-white text-[10px]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {/* Small button to open volume slider without muting */}
                {!showVolumeSlider && !muted && (
                  <button
                    onClick={() => setShowVolumeSlider(true)}
                    className="text-white/60 hover:text-white text-[10px] w-4 h-4 flex items-center justify-center"
                    title="Volume slider"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                )}
              </div>

              <div className="flex-1" />

              {/* Speed */}
              <div className="relative">
                <button
                  onClick={() => setShowRateMenu((s) => !s)}
                  disabled={!canControl}
                  className="h-8 px-2 flex items-center gap-1 text-white hover:bg-white/10 rounded-md transition-colors text-[11px] font-mono disabled:opacity-40"
                >
                  <Gauge className="w-3.5 h-3.5" />
                  {playbackRate}x
                </button>
                {showRateMenu && (
                  <div className="absolute bottom-9 right-0 bg-black/90 border border-white/10 rounded-md py-1 min-w-16">
                    {PLAYBACK_RATES.map((r) => (
                      <button
                        key={r}
                        onClick={() => changeRate(r)}
                        className={`w-full px-3 py-1 text-[11px] font-mono text-left hover:bg-white/10 ${
                          r === playbackRate ? "text-primary" : "text-white/80"
                        }`}
                      >
                        {r}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Loop */}
              <button
                onClick={() => setLoop((l) => !l)}
 className={`w-8 h-8 flex items-center justify-center transition-colors ${
                  loop ? "text-primary bg-primary/20" : "text-white/80 hover:bg-white/10"
                }`}
                title={loop ? "Loop ON" : "Loop OFF"}
              >
                <Repeat className="w-4 h-4" />
              </button>

              {/* Quality — only for YouTube */}
              {videoState.type === "youtube" && (
                <div className="relative">
                  <button
                    onClick={() => setShowQualityMenu((s) => !s)}
                    className="h-8 px-2 flex items-center gap-1 text-white hover:bg-white/10 rounded-md transition-colors text-[11px]"
                    title="Quality"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                  {showQualityMenu && (
                    <div className="absolute bottom-9 right-0 bg-black/90 border border-white/10 rounded-md py-1 min-w-20">
                      {[
                        { label: "Auto", value: "default" },
                        { label: "1080p", value: "hd1080" },
                        { label: "720p", value: "hd720" },
                        { label: "480p", value: "large" },
                        { label: "360p", value: "medium" },
                      ].map((q) => (
                        <button
                          key={q.value}
                          onClick={() => {
                            if (ytRef.current) {
                              try { ytRef.current.setPlaybackQuality(q.value); } catch {}
                            }
                            setSelectedQuality(q.value);
                            setShowQualityMenu(false);
                          }}
                          className={`w-full px-3 py-1 text-[11px] text-left hover:bg-white/10 ${
                            selectedQuality === q.value ? "text-primary font-semibold" : "text-white/80"
                          }`}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Picture-in-Picture — only show if supported and native video (not YouTube) */}
              {pipSupported && videoState.type !== "youtube" && videoState.url && (
                <button
                  onClick={togglePip}
                  className={`w-8 h-8 flex items-center justify-center transition-colors ${pipActive ? "text-[#78B4FF]" : "text-white/80 hover:text-white"}`}
                  title={pipActive ? "Exit Picture-in-Picture" : "Picture-in-Picture"}
                >
                  <PictureInPicture2 className="w-4 h-4" />
                </button>
              )}

              <button
                onClick={toggleFullscreen}
 className="w-8 h-8 flex items-center justify-center text-white transition-colors"
                title={theater ? "Exit fullscreen" : "Fullscreen"}
              >
                {theater ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bookmarks popover */}
      {showBookmarks && (
        <div className="absolute top-12 right-3 w-72 bg-card border border-white/10 rounded-lg p-4 z-30 animate-fade-in shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground">Bookmarks</span>
            <button onClick={() => setShowBookmarks(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {canControl && (
            <div className="flex gap-1.5 mb-2">
              <input
                value={bookmarkLabel}
                onChange={(e) => setBookmarkLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addBookmark()}
                placeholder={`Mark ${formatTime(currentTime)}`}
                maxLength={60}
                className="flex-1 glass-input rounded-xl px-3 py-2 text-xs outline-none"
              />
              <button
                onClick={addBookmark}
                className="px-3 glass-button-primary text-xs font-semibold rounded-xl flex items-center gap-1"
              >
                <Bookmark className="w-3 h-3" /> Add
              </button>
            </div>
          )}
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {bookmarks.length === 0 ? (
              <p className="text-[11px] text-muted-foreground text-center py-2">No bookmarks yet.</p>
            ) : (
              bookmarks.map((bm) => (
                <button
                  key={bm.id}
                  onClick={() => {
                    onSeek(bm.time);
                    setShowBookmarks(false);
                  }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded hover:bg-secondary text-[11px]"
                >
                  <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="font-mono text-muted-foreground shrink-0">{formatTime(bm.time)}</span>
                  <span className="text-foreground truncate">{bm.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

